/**
 * /certificacao — CATÁLOGO das 6 Certificações da FEO.
 *
 * Fora de ação: catálogo padrão (Ombro e Joelho no lote promocional).
 * Durante a Semana da Fisioterapia: mesma identidade + barra superior da ação,
 * faixa de oferta no bloco de checkout e todas as 6 a R$197 (72% OFF).
 * A versão fora de ação fica sempre disponível em /certificacao-bkp.
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import { redirecionaCatalogoBlackfeo } from "@/lib/black-november/ofertas-lps";

import {
  CertificacaoCatalogo,
  DESC,
  LP_OFICIAL,
  OG_IMAGE,
  TITLE,
  URL as CANONICAL,
} from "@/components/lps/CertificacaoCatalogoPage";
import { CERT_LIST, CERT_META } from "@/lib/certificacao/config";
import { certOfertaAtiva, QUADRIL_OFERTA } from "@/lib/certificacao/oferta";
import { getAcoesStateFn } from "@/lib/campanhas/acoes.functions";

export const Route = createFileRoute("/certificacao")({
  validateSearch: (search: Record<string, unknown>): { preview?: boolean } =>
    search.preview === "1" || search.preview === 1 || search.preview === true
      ? { preview: true }
      : {},
  beforeLoad: ({ location }) => {
    const destino = redirecionaCatalogoBlackfeo(location.searchStr, Date.now());
    if (destino) throw redirect({ href: destino });
  },
  loader: () => getAcoesStateFn(),
  head: ({ loaderData }) => {
    const acao = Boolean(loaderData?.semanadafisio?.ativa);
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESC },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESC },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:image", content: OG_IMAGE },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: OG_IMAGE },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESC },
        { name: "robots", content: "index,follow" },
      ],
      links: [{ rel: "canonical", href: CANONICAL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: TITLE,
            url: CANONICAL,
            description: DESC,
            mainEntity: {
              "@type": "ItemList",
              itemListElement: [...CERT_LIST.map((c) => ({ ...c, oferta: certOfertaAtiva(c.slug, acao) })), { ...CERT_META.quadril, oferta: QUADRIL_OFERTA }].map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "Course",
                  name: c.nome,
                  url: `https://fisioemortopedia.com.br${c.path}`,
                  description: c.seoDescription,
                  provider: {
                    "@type": "EducationalOrganization",
                    name: "Fisio em Ortopedia",
                    url: "https://fisioemortopedia.com.br",
                  },
                  offers: {
                    "@type": "Offer",
                    price: c.oferta.aVistaValor.replace("R$", ""),
                    priceCurrency: "BRL",
                    availability: "https://schema.org/InStock",
                    url: `https://fisioemortopedia.com.br${c.path}`,
                  },
                },
              })),
            },
          }),
        },
      ],
    };
  },
  component: CertificacaoRoute,
});

function CertificacaoRoute() {
  const acoes = Route.useLoaderData();
  const { preview } = Route.useSearch();
  // ?preview=1 → revisão interna do catálogo com a oferta da ação.
  return <CertificacaoCatalogo acao={Boolean(acoes?.semanadafisio?.ativa) || preview === true} />;
}
